import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check } from 'lucide-react';
import * as React from 'react';
import { useSearchParams } from 'react-router-dom';

import {
  Alert, AlertDescription, Badge, Button, Card, CardContent, Combobox, Input, Label, Skeleton,
} from '@/components/cargofl-ui';
import { PageContainer } from '@/components/shell/page-container';
import { PageHeader } from '@/components/shell/page-header';
import { api, ApiError, toastFailure } from '@/lib/api';
import {
  fetchConnectUrl, useCalendarConnection, useDisconnectCalendar, useSubmitCalendarCode,
} from '@/lib/calendar-api';
import { SESSION_QUERY_KEY, type Session } from '@/lib/session';

interface Capabilities {
  canChangePassword: boolean;
  signsInWithGoogle: boolean;
  passwordLoginEnabled: boolean;
}

/** A short list rather than every IANA zone: these are the ones anybody here needs. */
const ZONES = [
  'Asia/Kolkata',
  'Asia/Dubai',
  'Europe/London',
  'Europe/Berlin',
  'America/New_York',
  'America/Los_Angeles',
  'Australia/Sydney',
  'UTC',
];

export function ProfilePage({ session }: { session: Session }) {
  const { data: caps, isPending } = useQuery({
    queryKey: ['profile', 'capabilities'],
    queryFn: () => api.get<Capabilities>('/profile/capabilities'),
  });

  return (
    <PageContainer>
      <PageHeader title="My profile" description="Your name, timezone, password and API tokens." />
      <Details session={session} />
      {isPending ? <Skeleton className="h-56 w-full" /> : <Password caps={caps} />}
      <GoogleCalendar />
      <ApiTokens session={session} />
    </PageContainer>
  );
}

/**
 * Connect/disconnect Google Calendar, and the OAuth return leg.
 *
 * The redirect URI is `${APP_BASE_URL}/profile` (see calendar.controller.ts) -
 * /profile is a FLAT route with no nested children (App.tsx), so there is no
 * `/profile/calendar/callback` to land on. Google sends the browser back here
 * with `?code=`, and this component is what notices it, exchanges it, and
 * scrubs the param so a refresh does not resubmit a code that has already been
 * spent server-side.
 */
function GoogleCalendar() {
  const [params, setParams] = useSearchParams();
  const { data: connection, isPending } = useCalendarConnection();
  const submitCode = useSubmitCalendarCode();
  const disconnect = useDisconnectCalendar();
  const [error, setError] = React.useState<string | null>(null);
  const [connecting, setConnecting] = React.useState(false);

  const code = params.get('code');
  // Google echoes `state` back verbatim - see the comment on completeConnection
  // in connection.service.ts for why this has to round-trip and be checked, not
  // just carried along. Both are required together: a code with no state (or
  // vice versa) cannot be exchanged.
  const state = params.get('state');
  // Google sends this INSTEAD of a code when somebody cancels the consent
  // screen - `access_denied` is the ordinary "changed my mind" case, not a
  // fault. Without reading it the page stays silent and the parameter sits in
  // the URL, so the person is told nothing and the address bar still claims
  // something happened.
  const oauthError = params.get('error');
  // Guards against double-submit in strict mode (which mounts effects twice)
  // and against the code still sitting in `params` for one extra render after
  // the clear below is requested.
  const submitted = React.useRef(false);

  React.useEffect(() => {
    if (oauthError && !submitted.current) {
      submitted.current = true;
      setError(
        oauthError === 'access_denied'
          ? 'Connecting was cancelled, so nothing changed. You can try again whenever you like.'
          : `Google refused the connection (${oauthError}).`,
      );
      const next = new URLSearchParams(params);
      next.delete('error');
      next.delete('state');
      setParams(next, { replace: true });
      return;
    }
    if (!code || !state || submitted.current) return;
    submitted.current = true;
    setError(null);
    submitCode
      .mutateAsync({ code, state })
      .catch((err) => {
        setError(err instanceof ApiError ? err.message : 'Could not finish connecting your calendar.');
      })
      .finally(() => {
        // Clear `code`/`state` regardless of outcome: a spent code cannot be
        // exchanged again, so leaving it in the URL only means a refresh fails
        // a second time in the same way.
        const next = new URLSearchParams(params);
        next.delete('code');
        next.delete('state');
        setParams(next, { replace: true });
      });
    // Only `code`/`state`/`error` should re-trigger this - `params`/`setParams`
    // change on every navigation and would otherwise re-run the exchange.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code, state, oauthError]);

  const connect = async () => {
    setError(null);
    setConnecting(true);
    try {
      const { url } = await fetchConnectUrl();
      window.location.href = url;
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not start connecting your calendar.');
      setConnecting(false);
    }
  };

  if (isPending) {
    return (
      <Card>
        <CardContent className="py-5">
          <Skeleton className="h-24 w-full" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent className="space-y-3 py-5">
        <h2 className="text-base font-semibold">Google Calendar</h2>

        {connection?.connected ? (
          <div className="space-y-3">
            <p className="max-w-lg text-sm leading-relaxed text-muted-foreground">
              Connected{connection.connectedAt ? ` ${new Date(connection.connectedAt).toLocaleDateString()}` : ''}.
              {' '}
              {connection.lastSyncedAt
                ? `Last read ${new Date(connection.lastSyncedAt).toLocaleString('en-IN')}.`
                : 'Not read yet.'}
            </p>
            <Button hint={"Stop reading your Google Calendar"}
              variant="outline"
              disabled={disconnect.isPending}
              onClick={async () => {
                setError(null);
                try {
                  await disconnect.mutateAsync();
                } catch (err) {
                  setError(err instanceof ApiError ? err.message : 'Could not disconnect your calendar.');
                }
              }}
            >
              {disconnect.isPending ? 'Disconnecting…' : 'Disconnect'}
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="max-w-lg text-sm leading-relaxed text-muted-foreground">
              We read the titles and times of meetings in your primary calendar, only
              while you have a week open. We never write to your calendar and never
              store your meetings.
            </p>
            <Button hint={"Connect your Google Calendar (read-only) to fill in meetings"} disabled={connecting} onClick={() => void connect()}>
              {connecting ? 'Redirecting…' : 'Connect'}
            </Button>
          </div>
        )}

        {error && (
          <Alert className="border-destructive/30">
            <AlertDescription className="text-sm leading-relaxed">{error}</AlertDescription>
          </Alert>
        )}
      </CardContent>
    </Card>
  );
}

function Details({ session }: { session: Session }) {
  const queryClient = useQueryClient();
  const [displayName, setDisplayName] = React.useState(session.displayName);
  const [alias, setAlias] = React.useState(session.alias ?? '');
  const [timezone, setTimezone] = React.useState(session.timezone);
  const [saved, setSaved] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const save = useMutation({
    mutationFn: (body: unknown) => api.patch('/profile', body),
    onSuccess: () => {
      // The session carries the name shown in the rail and the top bar, so it has
      // to be refetched or the page keeps showing the old one until a reload.
      void queryClient.invalidateQueries({ queryKey: SESSION_QUERY_KEY });
      setSaved(true);
    },
  });

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setSaved(false);
    try {
      await save.mutateAsync({ displayName, alias: alias.trim() || null, timezone });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save that.');
    }
  };

  return (
    <Card>
      <CardContent className="py-5">
        <form onSubmit={submit} className="max-w-lg space-y-4">
          <div className="space-y-1.5">
            <Label>Work email</Label>
            {/* Read-only: this is what Google matches on and what somebody signs in
                with, so changing it is an administrator's decision, not a
                preference. */}
            <Input value={session.email} disabled />
            <p className="text-xs text-muted-foreground">
              This is what you sign in with. Ask HR if it needs changing.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="p-name">Full name</Label>
              <Input
                id="p-name"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="p-alias">Short name</Label>
              <Input id="p-alias" value={alias} onChange={(e) => setAlias(e.target.value)} />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Timezone</Label>
            <Combobox
              value={timezone}
              onChange={setTimezone}
              options={ZONES.map((z) => ({ value: z, label: z.replace('_', ' ') }))}
              placeholder="Choose a timezone"
              searchPlaceholder="Search timezones"
            />
            <p className="text-xs leading-relaxed text-muted-foreground">
              Used for the clock times on your entries. Which day a week belongs to is
              decided by the company calendar, not this.
            </p>
          </div>

          <div className="space-y-1.5">
            <Label>Role</Label>
            <div>
              <Badge variant="outline">{session.role.toLowerCase()}</Badge>
            </div>
            <p className="text-xs text-muted-foreground">
              Set by HR. Nothing on this page can change what you have access to.
            </p>
          </div>

          {error && (
            <Alert className="border-destructive/30">
              <AlertDescription className="text-sm">{error}</AlertDescription>
            </Alert>
          )}
          {saved && (
            <Alert>
              <AlertDescription className="flex items-center gap-2 text-sm">
                <Check className="size-4" />
                Saved.
              </AlertDescription>
            </Alert>
          )}

          <Button type="submit" disabled={save.isPending}>
            {save.isPending ? 'Saving…' : 'Save changes'}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}

function Password({ caps }: { caps: Capabilities | undefined }) {
  const [current, setCurrent] = React.useState('');
  const [next, setNext] = React.useState('');
  const [confirm, setConfirm] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);
  const [done, setDone] = React.useState(false);

  const change = useMutation({
    mutationFn: (body: unknown) => api.post('/profile/password', body),
  });

  if (!caps?.canChangePassword) {
    return (
      <Card>
        <CardContent className="py-5">
          <p className="text-sm font-semibold">Password</p>
          <p className="mt-1 max-w-lg text-sm leading-relaxed text-muted-foreground">
            {caps?.signsInWithGoogle
              ? 'You sign in with Google, so there is no password here to change.'
              : caps && !caps.passwordLoginEnabled
                ? 'Password sign-in is turned off. Sign in with Google.'
                : 'You have no password set. Ask HR to set one, or sign in with Google.'}
          </p>
        </CardContent>
      </Card>
    );
  }

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setDone(false);
    // Checked here as well as on the server: a mistyped confirmation is the common
    // case, and it does not need a round trip to notice.
    if (next !== confirm) {
      setError('The two new passwords do not match.');
      return;
    }
    try {
      await change.mutateAsync({ currentPassword: current, newPassword: next });
      setCurrent('');
      setNext('');
      setConfirm('');
      setDone(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not change your password.');
    }
  };

  return (
    <Card>
      <CardContent className="py-5">
        <form onSubmit={submit} className="max-w-lg space-y-4">
          <p className="text-sm font-semibold">Change password</p>

          <div className="space-y-1.5">
            <Label htmlFor="pw-current">Current password</Label>
            <Input
              id="pw-current"
              type="password"
              autoComplete="current-password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              required
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="pw-new">New password</Label>
            <Input
              id="pw-new"
              type="password"
              autoComplete="new-password"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              required
            />
            <p className="text-xs text-muted-foreground">
              At least 12 characters. A short phrase of a few words works well and is
              easier to remember than something with a symbol in it.
            </p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="pw-confirm">New password again</Label>
            <Input
              id="pw-confirm"
              type="password"
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
            />
          </div>

          {error && (
            <Alert className="border-destructive/30">
              <AlertDescription className="text-sm leading-relaxed">{error}</AlertDescription>
            </Alert>
          )}
          {done && (
            <Alert>
              <AlertDescription className="flex items-center gap-2 text-sm">
                <Check className="size-4" />
                Password changed. You stay signed in here, and on your other devices.
              </AlertDescription>
            </Alert>
          )}

          <Button type="submit" disabled={change.isPending}>
            {change.isPending ? 'Changing…' : 'Change password'}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}

/**
 * API tokens: issue, see, revoke.
 *
 * On this screen rather than an admin one because a token acts as its owner, so
 * the person who has to live with it is the person who should be able to see and
 * kill it. And it exists at all because the previous system had four tokens that
 * nobody could account for - the fix for that is not fewer tokens, it is a list
 * somebody can actually read.
 */
function ApiTokens({ session }: { session: Session }) {
  const queryClient = useQueryClient();
  const [name, setName] = React.useState('');
  const [days, setDays] = React.useState('90');
  const [issued, setIssued] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  const { data: tokens } = useQuery({
    queryKey: ['profile', 'tokens'],
    queryFn: () => api.get<TokenRow[]>('/profile/tokens'),
  });

  const create = useMutation({
    mutationFn: (body: unknown) => api.post<{ token: string }>('/profile/tokens', body),
    onSuccess: (result) => {
      // Shown once, and there is no way to show it again - so it stays on screen
      // until the person dismisses it rather than vanishing on the next render.
      setIssued(result.token);
      setName('');
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ['profile', 'tokens'] });
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Could not create the token.'),
  });

  const revoke = useMutation({
    mutationFn: (id: string) => api.delete(`/profile/tokens/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['profile', 'tokens'] }),
    onError: toastFailure('Could not revoke that token'),
  });

  const elevated = session.role === 'HR' || session.role === 'ADMIN';

  return (
    <Card>
      <CardContent className="space-y-4 py-5">
        <div>
          <h2 className="text-base font-semibold">API tokens</h2>
          <p className="mt-1 max-w-prose text-sm text-muted-foreground">
            For filling your timesheet from a script instead of the browser. A token acts as
            you: it can do what you can do, and nothing more.{' '}
            {elevated && (
              <strong className="text-foreground">
                Your account is {session.role} — a token of yours is an administrative
                credential. Treat it like your password.
              </strong>
            )}
          </p>
        </div>

        {issued && (
          <Alert>
            <AlertDescription className="space-y-2">
              <p className="text-sm font-medium">
                Copy this now. It is not stored and cannot be shown again.
              </p>
              <code className="block break-all rounded bg-muted px-3 py-2 font-mono text-xs">
                {issued}
              </code>
              <div className="flex gap-2">
                <Button hint={"Copy the token"} size="sm" variant="outline" onClick={() => void navigator.clipboard?.writeText(issued)}>
                  Copy
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setIssued(null)}>
                  Done
                </Button>
              </div>
            </AlertDescription>
          </Alert>
        )}

        <form
          className="flex flex-wrap items-end gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            const parsed = Number(days);
            if (!name.trim()) return setError('Give the token a name, so it can be recognised later.');
            if (!Number.isInteger(parsed) || parsed < 1 || parsed > 365) {
              return setError('Expiry must be between 1 and 365 days.');
            }
            create.mutate({ name: name.trim(), days: parsed });
          }}
        >
          <div className="space-y-1.5">
            <Label htmlFor="token-name">What is it for</Label>
            <Input
              id="token-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Timesheet fill script"
              className="w-64"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="token-days">Expires in (days)</Label>
            <Input
              id="token-days"
              value={days}
              onChange={(e) => setDays(e.target.value)}
              className="w-28"
            />
          </div>
          <Button hint={"Create a personal API token"} type="submit" disabled={create.isPending}>
            {create.isPending ? 'Creating…' : 'Create token'}
          </Button>
        </form>

        {error && (
          <Alert className="border-destructive/30">
            <AlertDescription className="text-sm">{error}</AlertDescription>
          </Alert>
        )}

        {tokens && tokens.length > 0 && (
          <div className="divide-y rounded-md border">
            {tokens.map((token) => (
              <div key={token.id} className="flex flex-wrap items-center gap-3 px-4 py-3 text-sm">
                <span className="font-medium">{token.name}</span>
                <code className="font-mono text-xs text-muted-foreground">{token.prefix}…</code>
                <Badge variant={token.state === 'active' ? 'default' : 'secondary'}>
                  {token.state}
                </Badge>
                <span className="text-xs text-muted-foreground">
                  {/* "never used" is the interesting state: an issued token nobody
                      wired up is one to revoke. */}
                  {token.lastUsedAt
                    ? `last used ${new Date(token.lastUsedAt).toLocaleDateString()}`
                    : 'never used'}
                  {' · '}
                  {token.state === 'revoked'
                    ? `revoked ${new Date(token.revokedAt!).toLocaleDateString()}`
                    : `expires ${new Date(token.expiresAt).toLocaleDateString()}`}
                </span>
                {token.state === 'active' && (
                  <Button hint={"Stop this token working"}
                    size="sm"
                    variant="outline"
                    className="ml-auto"
                    disabled={revoke.isPending}
                    onClick={() => revoke.mutate(token.id)}
                  >
                    Revoke
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}

        <details className="text-sm text-muted-foreground">
          <summary className="cursor-pointer font-medium text-foreground">
            How to use it
          </summary>
          <pre className="mt-2 overflow-x-auto rounded bg-muted p-3 font-mono text-xs leading-relaxed">{`# fill one day
curl -X POST ${window.location.origin}/api/timesheet/entries \\
  -H "Authorization: Bearer cfl_..." -H "Content-Type: application/json" \\
  -d '{"workDate":"2026-09-01","projectId":"...","activityId":"...","durationSeconds":28800}'

# fill a date range with the same entry, skipping weekends and holidays
curl -X POST ${window.location.origin}/api/timesheet/bulk \\
  -H "Authorization: Bearer cfl_..." -H "Content-Type: application/json" \\
  -d '{"from":"2026-09-01","to":"2026-09-30","projectId":"...","activityId":"...","durationSeconds":28800}'

# the ids come from here
curl ${window.location.origin}/api/catalog/projects -H "Authorization: Bearer cfl_..."`}</pre>
          <p className="mt-2">
            Every rule applies exactly as it does in the browser: a locked day, a submitted
            week, a hidden project and a closed month are all refused, and the response says
            which day it skipped and why.
          </p>
        </details>
      </CardContent>
    </Card>
  );
}

interface TokenRow {
  id: string;
  name: string;
  prefix: string;
  state: 'active' | 'expired' | 'revoked';
  expiresAt: string;
  lastUsedAt: string | null;
  revokedAt: string | null;
  createdAt: string;
}
